import apiClient from '../utils/apiClient';
import { Card } from 'shared/models';

const API_URL = '/api/cards';

const cardService = {
  // Get all cards with optional pagination and filtering
  getCardsByPage: async (page = 1, limit = 50, filters = {}) => {
    const params = {
      page,
      limit,
      ...filters
    };
    
    const response = await apiClient.get(API_URL, { params });
    
    return {
      cards: (response.data.cards || []).map(data => new Card(data)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalCards: response.data.totalCards || 0
    };
  },
  
  // Get card by ID
  getCardById: async (cardId) => {
    const response = await apiClient.get(`${API_URL}/${cardId}`);
    
    // Convert to Card instance
    return new Card(response.data.card);
  },
  
  // Get cards by insert set with pagination
  getCardsByInsertSetByPage: async (insertSet, page = 1, limit = 50) => {
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/insert-set/${insertSet.id}`, { params });
    
    return {
      cards: (response.data.cards || []).map(data => new Card(data)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalCards: response.data.totalCards || 0
    };
  },
  
  // Get only user's cards from an insert set, including inventory and wishlist data
  getUserCardsInInsertSetByPage: async (insertSet, page = 1, limit = 50) => {
    const params = {
      page,
      limit,
      includeUserData: true  // Signal backend to include inventory and wishlist data
    };
    
    const response = await apiClient.get(`${API_URL}/user/insert-set/${insertSet.id}`, { 
      params
    });
    
    return {
      cards: (response.data.cards || []).map(data => new Card(data)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalCards: response.data.totalCards || 0
    };
  },
  
  // Get cards from a specific user's collection for an insert set
  getUserCollectionCardsInInsertSetByPage: async (userId, insertSet, page = 1, limit = 50) => {
    const params = {
      page,
      limit,
      userId
    };
    
    const response = await apiClient.get(`${API_URL}/collection/insert-set/${insertSet.id}`, { 
      params
    });
    
    return {
      cards: (response.data.cards || []).map(data => new Card(data)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalCards: response.data.totalCards || 0
    };
  },
  
  // Get cards by product
  getCardsByProductByPage: async (product, page = 1, limit = 50) => {
    const params = {
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/product/${product.id}`, { params });
    
    return {
      cards: (response.data.cards || []).map(data => new Card(data)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalCards: response.data.totalCards || 0
    };
  },
  
  // Search cards with pagination
  searchCardsByPage: async (term, page = 1, limit = 50) => {
    const params = {
      term,
      page,
      limit
    };
    
    const response = await apiClient.get(`${API_URL}/search`, { params });
    
    return {
      cards: (response.data.cards || []).map(data => new Card(data)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalCards: response.data.totalCards || 0
    };
  },
  
  // Only public methods here - admin methods moved to cardAdminService
  
  // Upload a user-submitted card image
  uploadUserCardImage: async (cardId, imageFile) => {
    // Only proceed if user is logged in (apiClient will handle the auth token)
    const formData = new FormData();
    formData.append('cardId', cardId);
    formData.append('image', imageFile, `card-${cardId}.webp`);
    
    const response = await apiClient.post('/api/user-card-uploads', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  }
};

export default cardService;